import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AuthState, User } from "@/types/auth";
import { tokenStorage } from "@/utils/tokenStorage";

const initialState: AuthState = {
  error: null,
  isAuthenticated: false,
  // Start as true when a token exists so PrivateRoute shows a spinner
  // while AuthInitializer validates it, instead of immediately redirecting to login.
  isLoading: tokenStorage.hasToken(),
  user: null,
};

const authSlice = createSlice({
  initialState,
  name: "auth",
  reducers: {
    // Clear authentication state
    clearAuth: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.error = null;
      state.isLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Initialize user from token on app start
    initializeAuth: (state, action: PayloadAction<User>) => {
      state.isAuthenticated = true;
      state.user = action.payload;
      state.isLoading = false;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.error = action.payload;
    },
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (
      state,
      action: PayloadAction<{ token: string; user: User }>,
    ) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.error = null;
      // Note: Token storage is now handled in authService.login()
      // to properly respect the rememberMe preference
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.error = null;
      // Clear token from storage
      tokenStorage.removeToken();
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
});

export const {
  clearAuth,
  clearError,
  initializeAuth,
  loginFailure,
  loginStart,
  loginSuccess,
  logout,
  setLoading,
  updateUser,
} = authSlice.actions;

export default authSlice.reducer;
